import React, { useState, useEffect } from 'react';
import { listBanks, previewBankAccount, verifyAndSaveBankAccount } from '../../services/walletService';

const BankAccountForm = ({ existingAccount, onSaved }) => {
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [resolvedName, setResolvedName] = useState('');
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listBanks().then(setBanks).catch(() => setError('Could not load bank list'));
  }, []);

  const bankName = banks.find(b => b.code === bankCode)?.name || '';

  const handleCheckAccount = async () => {
    setError('');
    setResolvedName('');
    if (!bankCode || accountNumber.length < 10) {
      setError('Select a bank and enter a valid 10-digit account number');
      return;
    }
    setChecking(true);
    try {
      const name = await previewBankAccount(accountNumber, bankCode);
      setResolvedName(name);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not verify this account number');
    } finally {
      setChecking(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!disclaimerAgreed) {
      setError('Please confirm the account details are accurate');
      return;
    }
    setSaving(true);
    try {
      const saved = await verifyAndSaveBankAccount({ accountNumber, bankCode, bankName, disclaimerAgreed });
      onSaved(saved);
      setResolvedName('');
      setAccountNumber('');
      setBankCode('');
      setDisclaimerAgreed(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save bank account');
    } finally {
      setSaving(false);
    }
  };

  if (existingAccount) {
    return (
      <div className="bg-white p-4 rounded-lg shadow space-y-1">
        <p className="font-semibold">Payout account</p>
        <p className="text-sm">{existingAccount.accountName} — {existingAccount.bankName}</p>
        <p className="text-sm text-gray-500">{existingAccount.maskedAccountNumber}</p>
        <p className="text-xs text-gray-400">
          Verified {new Date(existingAccount.verifiedAt).toLocaleDateString()}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="bg-white p-4 rounded-lg shadow space-y-3">
      <p className="font-semibold">Add payout account</p>
      <p className="text-xs text-gray-500">
        We'll verify this account belongs to you before saving it, and your withdrawals will be sent here.
      </p>

      <select value={bankCode} onChange={(e) => { setBankCode(e.target.value); setResolvedName(''); }}
        className="w-full p-2 border rounded-lg text-sm">
        <option value="">Select bank</option>
        {banks.map(b => <option key={`${b.code}-${b.name}`} value={b.code}>{b.name}</option>)}
      </select>

      <input
        value={accountNumber}
        onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, '')); setResolvedName(''); }}
        placeholder="Account number"
        maxLength={10}
        className="w-full p-2 border rounded-lg text-sm"
      />

      <button type="button" onClick={handleCheckAccount} disabled={checking}
        className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm">
        {checking ? 'Checking...' : 'Check account name'}
      </button>

      {resolvedName && (
        <div className="bg-accent-lighter p-3 rounded-lg space-y-2">
          <p className="text-sm">Account name: <strong>{resolvedName}</strong></p>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={disclaimerAgreed} onChange={(e) => setDisclaimerAgreed(e.target.checked)}
              className="mt-0.5 shrink-0" />
            <span>I confirm this account belongs to me and the details above are accurate.</span>
          </label>
          <button type="submit" disabled={saving} className="bg-black text-white px-4 py-2 rounded-lg text-sm">
            {saving ? 'Saving...' : 'Save payout account'}
          </button>
        </div>
      )}

      {error && <p className="text-red-500 text-sm">{error}</p>}
    </form>
  );
};

export default BankAccountForm;
